import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { getDefaultAuthDestination } from "@/context/AuthContext";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useEffect, useState, useRef } from "react";
import { Mail } from "lucide-react";

export default function VerifyEmail() {
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const sentRef = useRef(false);

  // Send verification on first mount
  useEffect(() => {
    if (sentRef.current || !user || user.email_verified) return;
    sentRef.current = true;
    const send = async () => {
      try {
        const fbUser = auth.currentUser;
        if (fbUser && !fbUser.emailVerified) {
          await sendEmailVerification(fbUser);
          setSent(true);
        }
      } catch { /* ignore */ }
    };
    send();
  }, [user]);

  // Poll for verification
  useEffect(() => {
    if (!user || user.email_verified) return;
    const interval = setInterval(async () => {
      await checkAuth();
    }, 3000);
    return () => clearInterval(interval);
  }, [user, checkAuth]);

  const handleResend = async () => {
    try {
      const fbUser = auth.currentUser;
      if (fbUser) {
        await sendEmailVerification(fbUser);
        setSent(true);
        setTimeout(() => setSent(false), 3000);
      }
    } catch { /* ignore */ }
  };

  const handleContinue = () => {
    navigate(getDefaultAuthDestination(user));
  };

  if (user?.email_verified) {
    navigate(getDefaultAuthDestination(user), { replace: true });
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-volt/10 flex items-center justify-center mb-6">
          <Mail className="h-7 w-7 text-volt" />
        </div>
        <h2 className="font-heading text-2xl mb-2">{t("verify_email.title")}</h2>
        <p className="text-sm text-muted-foreground mb-2">
          {t("verify_email.sent_to", { email: user?.email })}
        </p>
        <p className="text-xs text-muted-foreground/70 mb-6">
          {t("verify_email.check_inbox")}
        </p>

        <div className="space-y-3">
          <button onClick={handleResend} className="btn-volt rounded-sm px-8 py-3 text-xs w-full">
            {sent ? t("verify_email.resent") : t("verify_email.resend")}
          </button>
          <button onClick={handleContinue} className="btn-outline rounded-sm px-8 py-3 text-xs w-full">
            {t("verify_email.continue_anyway")}
          </button>
        </div>

        <p className="text-[10px] text-muted-foreground/50 mt-4">
          {t("verify_email.logout_hint")}
        </p>
      </div>
    </div>
  );
}
